(function () {
  'use strict';

  /* The Beauty Finder ships as its own asset rather than inside theme.js: it is
     one page's behaviour, and the shared bundle is loaded on every request.
     It reads the theme's public helpers (window.Y10) and nothing private. */

  function formatMoney(cents, format) {
    if (cents == null) return '';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dollars)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount\s*\}\}/g, dollars + '.' + parts[1]);
  }

  /* Wishlist toggling itself is handled by the theme's delegated listener. Only
     the resting state of a freshly rendered button has to be painted here, from
     the same store the theme uses. */
  function updateWishlistButtons(scope) {
    var store = window.Y10 && window.Y10.wishlist;
    if (!store) return;
    var handles = store.getAll();
    (scope || document).querySelectorAll('[data-wishlist-button]').forEach(function (button) {
      var handle = button.getAttribute('data-product-handle');
      if (!handle) return;
      var active = handles.indexOf(handle) !== -1;
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('is-active', active);
      var label = active ? button.getAttribute('data-wishlist-remove-label') : button.getAttribute('data-wishlist-add-label');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  /* -------------------------------------------------------------------------
     Beauty Finder

     A short guided conversation that ends in a curated edit. The questions
     branch on the answers given, and every product comes from the live
     catalogue through the `finder` feed views, scored against the answers.

     Nothing here invents a product attribute. Where Shopify holds a fact —
     product type, collection, price, availability, image, and the custom
     metafields the catalogue actually carries (gender, concentration,
     perfume origin, makeup area) — it is used. Everything softer (a vibe, a
     finish, a shade family) is matched conservatively against words that are
     really in the product titles, and only ever as a tie-breaker.
     ------------------------------------------------------------------------- */

  var finderCollections = {
    perfumes: 'perfumes',
    arabicPerfumes: 'arabic-perfumes',
    makeup: 'makeup',
    makeupEyes: 'makeup-eyes',
    makeupLips: 'makeup-lips',
    makeupFace: 'makeup-face',
    makeupCheeks: 'makeup-cheeks',
    skincare: 'skincare',
    skincareCleansers: 'skincare-cleansers',
    skincareSerums: 'skincare-serums',
    skincareMoisturizers: 'skincare-moisturizers',
    skincareMasks: 'skincare-masks',
    skincareSpf: 'skincare-spf',
    skincareToners: 'skincare-toners',
    skincareEye: 'skincare-eye-care',
    haircare: 'haircare',
    hairShampoo: 'haircare-shampoo',
    hairConditioner: 'haircare-conditioner',
    hairMasks: 'haircare-masks',
    hairOils: 'haircare-oils',
    hairStyling: 'haircare-styling',
    hairTreatments: 'haircare-treatments',
    wigs: 'wigs-hair-extensions',
    body: 'body',
    bodyLotion: 'body-lotion',
    bodyMist: 'body-mist',
    bodyOils: 'body-oils',
    bodyWash: 'body-wash',
    tools: 'tools-brushes',
    beautyTools: 'beauty-tools',
    accessories: 'beauty-accessories',
    nails: 'nails',
    giftSets: 'gift-sets',
    valueSets: 'value-sets',
    miniSizes: 'mini-sizes',
    bestSellers: 'best-sellers',
    newIn: 'new-in'
  };

  var FINDER_FRAGRANCE_TYPES = [
    'Eau de Parfum',
    'Parfum',
    'Eau de Toilette',
    'Perfume Oil',
    'Other Fragrance'
  ];

  var FINDER_SHADE_CRITICAL = [
    'Foundation',
    'Concealer',
    'Contour',
    'Setting Powder',
    'Pressed Powder',
    'Hair Dye',
    'Artificial Nails',
    'Wig',
    'Lace Front',
    'Hair Bundle',
    'Closure'
  ];

  /* Types the catalogue reuses across categories: a "Serum" or a "Treatment"
     is as likely to be for hair as for skin, and the title alone cannot tell
     them apart. Those only qualify when the collection they came from says
     which category they belong to. */
  var FINDER_AMBIGUOUS_TYPES = ['Serum', 'Treatment', 'Hair Oil', 'Body Oil', 'Oil'];

  /* Questions that narrow an already chosen family. Their answer does not just
     widen the net, it says which corner of it the customer actually meant, so
     those types are scored above the rest of the family. */
  var FINDER_FOCUS_QUESTIONS = ['tools_nails', 'tools_makeup', 'tools_hair', 'hair_step'];

  /* Answers that say "I don't know". Two of them and the finder stops asking
     technical questions — uncertainty is information too. */
  var FINDER_OPEN_ANSWERS = ['surprise', 'not_sure', 'open', 'browsing', 'help_me'];

  var finderBudgets = {
    under_25: { min: 0, max: 2500, phrase: 'an under $25 budget' },
    to_50: { min: 2500, max: 5000, phrase: 'a $25 to $50 budget' },
    to_100: { min: 5000, max: 10000, phrase: 'a $50 to $100 budget' },
    over_100: { min: 10000, max: null, phrase: 'room to spend' },
    open: { min: null, max: null, phrase: 'no ceiling on the budget' }
  };

  function finderBudgetOptions(gift) {
    return [
      { value: 'under_25', label: 'Under $25' },
      { value: 'to_50', label: '$25 – $50' },
      { value: 'to_100', label: '$50 – $100' },
      { value: 'over_100', label: '$100+' },
      {
        value: 'open',
        label: gift ? 'Whatever suits them' : "Don't limit me — find the right match"
      }
    ];
  }

  var finderQuestions = {
    intent: {
      note: 'No wrong answer here.',
      title: 'What brought you to Y-10 today?',
      cta: 'continue',
      options: [
        {
          value: 'treat',
          label: 'I want to treat myself',
          icon: 'sparkle',
          phrase: 'something worth treating yourself to',
          profile: { keywords: ['luxe', 'luxury', 'gold', 'elite', 'prestige'], collections: [{ handle: finderCollections.bestSellers, weight: 6, role: 'discovery' }] }
        },
        {
          value: 'everyday',
          label: 'I need something for my everyday routine',
          icon: 'droplet',
          phrase: 'everyday wear',
          profile: { keywords: ['daily', 'everyday', 'classic'] }
        },
        {
          value: 'occasion',
          label: "I'm getting ready for something special",
          icon: 'diamond',
          phrase: 'an occasion',
          profile: { keywords: ['intense', 'elixir', 'extrait', 'glam', 'gold'] }
        },
        {
          value: 'gift',
          label: "I'm shopping for someone",
          icon: 'gift',
          branch: 'gift',
          phrase: 'a gift',
          profile: { gift: true }
        },
        {
          value: 'switch',
          label: 'I want to switch up my look',
          icon: 'bolt',
          phrase: 'a change',
          profile: { keywords: ['new', 'bold', 'limited'], collections: [{ handle: finderCollections.newIn, weight: 6, role: 'discovery' }] }
        },
        {
          value: 'not_sure',
          label: "I don't know yet — help me find something",
          icon: 'star',
          phrase: 'an open mind',
          profile: {}
        }
      ]
    },

    category: {
      note: "Let's narrow it down.",
      title: 'Where should we start?',
      cta: 'continue',
      options: [
        {
          value: 'skincare',
          label: 'Skincare',
          icon: 'droplet',
          branch: 'skincare',
          profile: { collections: [{ handle: finderCollections.skincare, weight: 14, role: 'primary' }] }
        },
        {
          value: 'makeup',
          label: 'Makeup',
          icon: 'lipstick',
          branch: 'makeup',
          profile: { collections: [{ handle: finderCollections.makeup, weight: 10, role: 'primary' }] }
        },
        {
          value: 'fragrance',
          label: 'Fragrance',
          icon: 'sparkle',
          branch: 'fragrance',
          profile: {
            types: FINDER_FRAGRANCE_TYPES,
            complementTypes: ['Body Mist', 'Body Spray', 'Perfume Oil'],
            collections: [
              { handle: finderCollections.perfumes, weight: 12, role: 'primary' },
              { handle: finderCollections.bodyMist, weight: 6, role: 'complement' }
            ]
          }
        },
        {
          value: 'hair',
          label: 'Hair',
          icon: 'leaf',
          branch: 'hair',
          profile: { collections: [{ handle: finderCollections.haircare, weight: 10, role: 'primary' }] }
        },
        {
          value: 'body',
          label: 'Body & self-care',
          icon: 'sun',
          branch: 'body',
          profile: { collections: [{ handle: finderCollections.body, weight: 12, role: 'primary' }] }
        },
        {
          value: 'tools',
          label: 'Tools & accessories',
          icon: 'grid',
          branch: 'tools',
          profile: { collections: [{ handle: finderCollections.tools, weight: 10, role: 'primary' }] }
        }
      ]
    },

    frag_vibe: {
      note: 'Good choice.',
      title: 'What kind of impression do you want to leave?',
      cta: 'thats_me',
      options: [
        { value: 'fresh', label: 'Fresh & effortless', phrase: 'fresh and effortless', profile: { vibe: ['fresh', 'aqua', 'blue', 'breeze', 'citrus', 'lime', 'lemon', 'marine', 'ocean', 'ice', 'cool', 'water', 'sport', 'mint'], searches: ['fresh perfume'] } },
        { value: 'soft', label: 'Soft & inviting', phrase: 'soft and inviting', profile: { vibe: ['musk', 'white', 'powder', 'cotton', 'silk', 'iris', 'soft', 'rose', 'peony', 'jasmine', 'blossom', 'lavender'], searches: ['musk perfume'] } },
        { value: 'sweet', label: 'Sweet & addictive', phrase: 'sweet and addictive', profile: { vibe: ['sweet', 'sugar', 'candy', 'vanilla', 'caramel', 'honey', 'pink', 'berry', 'cherry', 'peach', 'chocolate', 'delight'], searches: ['vanilla perfume'] } },
        { value: 'warm', label: 'Warm & sensual', phrase: 'warm and sensual', profile: { vibe: ['amber', 'warm', 'spice', 'saffron', 'cinnamon', 'tobacco', 'honey', 'velvet', 'sultan', 'musk'], searches: ['amber perfume'] } },
        { value: 'dark', label: 'Dark & mysterious', phrase: 'dark and memorable', profile: { vibe: ['oud', 'dark', 'black', 'night', 'noir', 'leather', 'smoke', 'incense', 'onyx', 'intense'], searches: ['oud perfume'] } },
        { value: 'rich', label: 'Rich & sophisticated', phrase: 'rich and sophisticated', profile: { vibe: ['gold', 'luxe', 'royal', 'elite', 'prestige', 'supremacy', 'imperial', 'grand', 'private', 'oud'], searches: ['luxury oud perfume'] } },
        { value: 'surprise', label: 'Surprise me', phrase: 'an open mind', profile: {} }
      ]
    },

    frag_occasion: {
      note: 'One more thing.',
      title: 'When would you wear it most?',
      cta: 'next_question',
      options: [
        { value: 'every_day', label: 'Every day', phrase: 'made for every day', profile: { concentration: ['Eau de Toilette', 'Eau de Parfum'], keywords: ['fresh', 'light', 'daily'] } },
        { value: 'daytime', label: 'Work / daytime', phrase: 'easy for daytime', profile: { concentration: ['Eau de Toilette', 'Eau de Parfum'], keywords: ['fresh', 'clean', 'light'] } },
        { value: 'date', label: 'Date night', phrase: 'built for date night', profile: { concentration: ['Eau de Parfum', 'Parfum'], keywords: ['intense', 'seduction', 'noir', 'velvet'] } },
        { value: 'evening', label: 'Evenings out', phrase: 'made for evenings', profile: { concentration: ['Eau de Parfum', 'Parfum'], keywords: ['intense', 'night', 'noir', 'oud'] } },
        { value: 'special', label: 'Special occasions', phrase: 'saved for occasions', profile: { concentration: ['Parfum', 'Eau de Parfum'], types: ['Parfum', 'Eau de Parfum', 'Perfume Oil'], keywords: ['elixir', 'extrait', 'gold', 'royal'] } },
        { value: 'anytime', label: 'Anytime', phrase: 'wearable anytime', profile: {} }
      ]
    },

    frag_family: {
      note: "We're getting close.",
      title: 'Which direction sounds most like you?',
      cta: 'find_match',
      options: [
        { value: 'citrus', label: 'Fresh / citrus', phrase: 'fresh and citrus-led', profile: { vibe: ['citrus', 'lemon', 'lime', 'bergamot', 'orange', 'grapefruit', 'fresh', 'aqua'], searches: ['citrus perfume'] } },
        { value: 'floral', label: 'Floral', phrase: 'floral', profile: { vibe: ['rose', 'jasmine', 'floral', 'flora', 'blossom', 'peony', 'lily', 'violet'], searches: ['rose perfume'] } },
        { value: 'fruity', label: 'Fruity', phrase: 'fruity', profile: { vibe: ['berry', 'peach', 'apple', 'mango', 'cherry', 'fruit', 'coconut', 'pineapple'], searches: ['fruity perfume'] } },
        { value: 'gourmand', label: 'Vanilla / gourmand', phrase: 'vanilla-warm', profile: { vibe: ['vanilla', 'caramel', 'chocolate', 'sugar', 'honey', 'praline', 'coffee'], searches: ['vanilla perfume'] } },
        { value: 'woody', label: 'Woody', phrase: 'woody', profile: { vibe: ['wood', 'sandal', 'cedar', 'patchouli', 'vetiver', 'oud'], searches: ['woody perfume'] } },
        { value: 'amber', label: 'Amber / oud', phrase: 'amber and oud', profile: { vibe: ['amber', 'oud', 'incense', 'musk', 'agarwood', 'bakhoor'], searches: ['oud perfume'] } },
        { value: 'open', label: "I'm open to anything", phrase: 'an open mind', profile: {} }
      ]
    },

    makeup_look: {
      note: 'Good choice.',
      title: "What's the look today?",
      cta: 'thats_me',
      options: [
        {
          value: 'no_makeup', label: 'No-makeup makeup', phrase: 'a no-makeup look',
          profile: {
            types: ['Foundation', 'Concealer', 'Lip Balm', 'Primer', 'Setting Spray', 'Eyebrow'],
            keywords: ['tinted', 'bb', 'natural', 'sheer', 'skin', 'blur'],
            area: 'Face',
            collections: [{ handle: finderCollections.makeupFace, weight: 12, role: 'primary' }],
            complementTypes: ['Lip Balm', 'Setting Spray', 'Eyebrow'],
            searches: ['tinted moisturizer']
          }
        },
        {
          value: 'everyday', label: 'Everyday polished', phrase: 'everyday polish',
          profile: {
            types: ['Foundation', 'Mascara', 'Lip Gloss', 'Eyebrow', 'Blush', 'Concealer'],
            collections: [{ handle: finderCollections.makeupFace, weight: 10, role: 'primary' }, { handle: finderCollections.makeupCheeks, weight: 6, role: 'complement' }],
            complementTypes: ['Blush', 'Mascara', 'Lip Gloss'],
            searches: ['everyday mascara']
          }
        },
        {
          value: 'soft_glam', label: 'Soft glam', phrase: 'soft glam',
          profile: {
            types: ['Eyeshadow', 'Highlighter', 'Lip Gloss', 'Blush', 'Makeup Palette', 'Setting Spray'],
            keywords: ['shimmer', 'glow', 'palette', 'soft'],
            collections: [{ handle: finderCollections.makeupEyes, weight: 10, role: 'primary' }, { handle: finderCollections.makeupCheeks, weight: 8, role: 'complement' }],
            complementTypes: ['Highlighter', 'Setting Spray', 'Lip Gloss'],
            searches: ['eyeshadow palette']
          }
        },
        {
          value: 'full_glam', label: 'Full glam', phrase: 'full glam',
          profile: {
            types: ['Foundation', 'Makeup Palette', 'False Lashes', 'Setting Powder', 'Contour', 'Lipstick', 'Highlighter'],
            keywords: ['full', 'hd', 'matte', 'palette', 'lash'],
            collections: [{ handle: finderCollections.makeupFace, weight: 10, role: 'primary' }, { handle: finderCollections.makeupEyes, weight: 8, role: 'complement' }],
            complementTypes: ['False Lashes', 'Setting Powder', 'Highlighter'],
            searches: ['full coverage foundation']
          }
        },
        {
          value: 'eyes', label: 'Statement eyes', phrase: 'statement eyes',
          profile: {
            types: ['Eyeshadow', 'Mascara', 'Eyeliner', 'False Lashes', 'Makeup Palette'],
            area: 'Eyes',
            collections: [{ handle: finderCollections.makeupEyes, weight: 14, role: 'primary' }],
            complementTypes: ['Eyeliner', 'False Lashes', 'Eyebrow'],
            searches: ['eyeliner']
          }
        },
        {
          value: 'lips', label: 'Bold lips', phrase: 'a bold lip',
          profile: {
            types: ['Lipstick', 'Lip Gloss', 'Lip Liner'],
            area: 'Lips',
            collections: [{ handle: finderCollections.makeupLips, weight: 14, role: 'primary' }],
            complementTypes: ['Lip Liner', 'Lip Balm'],
            searches: ['matte lipstick']
          }
        },
        {
          value: 'essentials', label: 'I just need the essentials', phrase: 'the essentials',
          profile: {
            types: ['Mascara', 'Lip Balm', 'Concealer', 'Eyebrow', 'Setting Spray'],
            collections: [{ handle: finderCollections.makeup, weight: 8, role: 'primary' }],
            complementTypes: ['Mascara', 'Concealer']
          }
        }
      ]
    },

    makeup_priority: {
      note: 'Got it.',
      title: 'What matters most?',
      cta: 'next_question',
      options: [
        { value: 'natural', label: 'Looking natural', phrase: 'a natural finish', profile: { keywords: ['natural', 'nude', 'sheer', 'skin', 'soft'] } },
        { value: 'wear', label: 'Long-lasting wear', phrase: 'long wear', profile: { keywords: ['long', 'lasting', '24', 'waterproof', 'transfer', 'matte', 'setting'] } },
        { value: 'coverage', label: 'Coverage', phrase: 'real coverage', profile: { keywords: ['coverage', 'full', 'hd', 'cover', 'concealer'] } },
        { value: 'glow', label: 'Glow', phrase: 'a glow', profile: { keywords: ['glow', 'dewy', 'luminous', 'radiant', 'illuminat', 'shimmer'] } },
        { value: 'easy', label: 'Easy application', phrase: 'easy application', profile: { keywords: ['stick', 'pen', 'easy', 'quick', 'one'] } },
        { value: 'new', label: 'Trying something new', phrase: 'something new', profile: { keywords: ['new', 'palette', 'limited', 'duo'] } }
      ]
    },

    makeup_finish: {
      note: 'One more thing.',
      title: 'What finish feels most like you?',
      cta: 'find_match',
      options: [
        { value: 'natural_finish', label: 'Natural', phrase: 'a natural finish', profile: { keywords: ['natural', 'satin', 'skin', 'soft'] } },
        { value: 'matte', label: 'Matte', phrase: 'a matte finish', profile: { keywords: ['matte', 'blur', 'velvet', 'powder'] } },
        { value: 'glowy', label: 'Glowy', phrase: 'a glowy finish', profile: { keywords: ['glow', 'dewy', 'luminous', 'radiant', 'illuminat'] } }
      ]
    },

    makeup_lip_shade: {
      note: 'One more thing.',
      title: 'What are you reaching for?',
      cta: 'find_match',
      options: [
        { value: 'nude', label: 'Nude', phrase: 'nude tones', profile: { vibe: ['nude', 'beige', 'caramel', 'toffee', 'bare'] } },
        { value: 'pink', label: 'Pink', phrase: 'pinks', profile: { vibe: ['pink', 'rose', 'blush', 'mauve'] } },
        { value: 'red', label: 'Red', phrase: 'a red lip', profile: { vibe: ['red', 'ruby', 'crimson', 'scarlet', 'cherry'] } },
        { value: 'brown', label: 'Brown', phrase: 'browns', profile: { vibe: ['brown', 'chocolate', 'cocoa', 'mocha', 'chestnut'] } },
        { value: 'berry', label: 'Berry', phrase: 'berry tones', profile: { vibe: ['berry', 'plum', 'wine', 'burgundy', 'merlot'] } },
        { value: 'surprise', label: 'Surprise me', phrase: 'an open mind', profile: {} }
      ]
    },

    skin_need: {
      note: 'Good choice.',
      title: 'If your skin could ask for one thing today, what would it be?',
      cta: 'thats_me',
      options: [
        {
          value: 'hydration', label: 'More hydration', phrase: 'more hydration',
          profile: {
            types: ['Moisturizer', 'Serum', 'Toner', 'Other Skincare'],
            keywords: ['hydra', 'moistur', 'water', 'hyaluronic', 'aloe', 'dewy'],
            collections: [{ handle: finderCollections.skincareMoisturizers, weight: 12, role: 'primary' }, { handle: finderCollections.skincareSerums, weight: 8, role: 'primary' }],
            complementTypes: ['Cleanser', 'SPF'],
            searches: ['hydrating moisturizer']
          }
        },
        {
          value: 'glow', label: 'A healthy glow', phrase: 'a healthy glow',
          profile: {
            types: ['Serum', 'Exfoliator', 'Mask', 'Other Skincare'],
            keywords: ['glow', 'vitamin c', 'bright', 'radian', 'illuminat'],
            collections: [{ handle: finderCollections.skincareSerums, weight: 12, role: 'primary' }, { handle: finderCollections.skincareMasks, weight: 8, role: 'complement' }],
            complementTypes: ['Cleanser', 'Moisturizer'],
            searches: ['brightening serum']
          }
        },
        {
          value: 'calm', label: 'A calmer feel', phrase: 'a calmer feel',
          profile: {
            types: ['Moisturizer', 'Toner', 'Mask', 'Other Skincare', 'Cleanser'],
            keywords: ['calm', 'sooth', 'sensitive', 'aloe', 'oat', 'gentle', 'cica'],
            collections: [{ handle: finderCollections.skincareToners, weight: 10, role: 'primary' }, { handle: finderCollections.skincareMoisturizers, weight: 10, role: 'primary' }],
            complementTypes: ['Cleanser', 'Moisturizer'],
            searches: ['gentle soothing moisturizer']
          }
        },
        {
          value: 'texture', label: 'Smoother-looking texture', phrase: 'smoother texture',
          profile: {
            types: ['Exfoliator', 'Serum', 'Toner', 'Other Skincare'],
            keywords: ['exfoliat', 'aha', 'bha', 'peel', 'scrub', 'smooth', 'retinol', 'resurfac'],
            collections: [{ handle: finderCollections.skincareSerums, weight: 10, role: 'primary' }, { handle: finderCollections.skincareToners, weight: 8, role: 'primary' }],
            complementTypes: ['Cleanser', 'Moisturizer'],
            searches: ['exfoliating serum']
          }
        },
        {
          value: 'breakouts', label: 'Help with breakouts', phrase: 'help with breakouts',
          profile: {
            types: ['Acne Treatment', 'Cleanser', 'Toner', 'Other Skincare'],
            keywords: ['acne', 'blemish', 'salicylic', 'tea tree', 'clear', 'spot', 'blackhead'],
            collections: [{ handle: finderCollections.skincareCleansers, weight: 10, role: 'primary' }, { handle: finderCollections.skincare, weight: 8, role: 'primary' }],
            complementTypes: ['Cleanser', 'Moisturizer'],
            searches: ['acne treatment']
          }
        },
        {
          value: 'simple', label: 'A simpler routine', phrase: 'a simpler routine',
          profile: {
            types: ['Cleanser', 'Moisturizer', 'SPF'],
            keywords: ['daily', 'gentle', 'all in one'],
            collections: [{ handle: finderCollections.skincareCleansers, weight: 10, role: 'primary' }, { handle: finderCollections.skincareMoisturizers, weight: 10, role: 'primary' }],
            complementTypes: ['SPF'],
            searches: ['daily cleanser']
          }
        },
        {
          value: 'everything', label: 'Honestly, a bit of everything', phrase: 'a bit of everything',
          profile: {
            types: ['Cleanser', 'Serum', 'Moisturizer', 'Toner', 'Mask', 'SPF', 'Exfoliator', 'Other Skincare'],
            complementTypes: ['SPF', 'Mask'],
            searches: ['skincare set']
          }
        }
      ]
    },

    skin_routine: {
      note: "We're getting close.",
      title: 'How much of a routine do you actually want?',
      cta: 'find_match',
      options: [
        { value: 'minimal', label: 'Keep it simple — 1 or 2 products', phrase: 'kept simple', profile: { depth: 1 } },
        { value: 'solid', label: 'A solid everyday routine', phrase: 'an everyday routine', profile: { depth: 2 } },
        { value: 'full', label: 'Give me the full routine', phrase: 'the full routine', profile: { depth: 3 } }
      ]
    },

    hair_need: {
      note: 'Good choice.',
      title: "What's your hair asking for right now?",
      cta: 'thats_me',
      options: [
        {
          value: 'moisture', label: 'Moisture', phrase: 'moisture', branch: 'hair_step',
          profile: {
            types: ['Conditioner', 'Leave-In', 'Hair Oil', 'Shampoo', 'Hair Mask'],
            keywords: ['moistur', 'hydrat', 'shea', 'coconut', 'curl', 'butter'],
            collections: [{ handle: finderCollections.hairConditioner, weight: 12, role: 'primary' }, { handle: finderCollections.hairOils, weight: 8, role: 'complement' }],
            complementTypes: ['Shampoo', 'Leave-In'],
            searches: ['moisturizing conditioner']
          }
        },
        {
          value: 'repair', label: 'Repair', phrase: 'repair', branch: 'hair_step',
          profile: {
            types: ['Treatment', 'Hair Mask', 'Leave-In', 'Conditioner'],
            keywords: ['repair', 'bond', 'protein', 'damage', 'keratin', 'strength'],
            collections: [{ handle: finderCollections.hairTreatments, weight: 12, role: 'primary' }, { handle: finderCollections.hairMasks, weight: 10, role: 'primary' }],
            complementTypes: ['Shampoo', 'Conditioner'],
            searches: ['bond repair treatment']
          }
        },
        {
          value: 'growth', label: 'Growth & scalp care', phrase: 'growth and scalp care', branch: 'hair_step',
          profile: {
            types: ['Scalp Care', 'Hair Oil', 'Treatment', 'Shampoo'],
            keywords: ['growth', 'rosemary', 'scalp', 'biotin', 'castor', 'thick', 'edge'],
            collections: [{ handle: finderCollections.hairOils, weight: 12, role: 'primary' }, { handle: finderCollections.hairTreatments, weight: 8, role: 'primary' }],
            complementTypes: ['Shampoo', 'Conditioner'],
            searches: ['hair growth oil']
          }
        },
        {
          value: 'styling', label: 'Styling & hold', phrase: 'styling', branch: 'hair_style_goal',
          profile: {
            types: ['Styling', 'Hair Gel', 'Hair Spray', 'Heat Protectant'],
            collections: [{ handle: finderCollections.hairStyling, weight: 14, role: 'primary' }],
            complementTypes: ['Heat Protectant', 'Hair Oil'],
            searches: ['styling gel']
          }
        },
        {
          value: 'color', label: 'Color', phrase: 'colour', branch: null,
          profile: {
            types: ['Hair Dye'],
            keywords: ['color', 'colour', 'dye', 'tint', 'bleach', 'permanent'],
            collections: [{ handle: finderCollections.haircare, weight: 8, role: 'primary' }],
            complementTypes: ['Conditioner', 'Hair Mask'],
            searches: ['hair color']
          }
        },
        {
          value: 'wigs', label: 'Wigs & extensions', phrase: 'wigs and extensions', branch: 'wig_priority',
          profile: {
            types: ['Wig', 'Lace Front', 'Hair Bundle', 'Hair Extension', 'Closure', 'Ponytail', 'Other Hair Piece'],
            collections: [{ handle: finderCollections.wigs, weight: 14, role: 'primary' }],
            complementTypes: ['Wig Accessories'],
            searches: ['lace front wig']
          }
        },
        {
          value: 'hair_tools', label: 'Tools & accessories', phrase: 'hair tools', branch: null,
          profile: {
            types: ['Hair Tools', 'Wig Accessories', 'Other Accessories'],
            collections: [{ handle: finderCollections.tools, weight: 10, role: 'primary' }, { handle: finderCollections.beautyTools, weight: 8, role: 'primary' }],
            searches: ['hair tools']
          }
        },
        {
          value: 'refresh', label: 'A complete refresh', phrase: 'a full reset', branch: 'hair_step',
          profile: {
            types: ['Shampoo', 'Conditioner', 'Hair Mask', 'Hair Oil', 'Leave-In', 'Treatment'],
            collections: [{ handle: finderCollections.hairShampoo, weight: 10, role: 'primary' }, { handle: finderCollections.hairConditioner, weight: 10, role: 'primary' }],
            complementTypes: ['Hair Mask', 'Hair Oil'],
            searches: ['shampoo and conditioner']
          }
        }
      ]
    },

    hair_step: {
      note: 'Got it.',
      title: 'Where do you want to start?',
      cta: 'find_match',
      options: [
        { value: 'wash', label: 'Shampoo & conditioner', phrase: 'starting at the wash', profile: { types: ['Shampoo', 'Conditioner'], collections: [{ handle: finderCollections.hairShampoo, weight: 10, role: 'primary' }, { handle: finderCollections.hairConditioner, weight: 10, role: 'primary' }], complementTypes: ['Hair Mask', 'Leave-In'] } },
        { value: 'treatment', label: 'Masks & treatments', phrase: 'with a treatment', profile: { types: ['Hair Mask', 'Treatment'], collections: [{ handle: finderCollections.hairMasks, weight: 12, role: 'primary' }, { handle: finderCollections.hairTreatments, weight: 10, role: 'primary' }], complementTypes: ['Shampoo', 'Conditioner'] } },
        { value: 'leave_in', label: 'Oils & leave-ins', phrase: 'with oils and leave-ins', profile: { types: ['Hair Oil', 'Leave-In', 'Scalp Care'], collections: [{ handle: finderCollections.hairOils, weight: 12, role: 'primary' }], complementTypes: ['Conditioner', 'Hair Mask'] } },
        { value: 'whole', label: 'The whole routine', phrase: 'the whole routine', profile: { depth: 3, collections: [{ handle: finderCollections.haircare, weight: 8, role: 'primary' }] } }
      ]
    },

    hair_style_goal: {
      note: 'Got it.',
      title: "What's the goal?",
      cta: 'find_match',
      options: [
        { value: 'sleek', label: 'Sleek & smooth', phrase: 'sleek and smooth', profile: { keywords: ['sleek', 'smooth', 'edge', 'laid', 'silk', 'gel', 'frizz'] } },
        { value: 'curls', label: 'Curls / definition', phrase: 'defined curls', profile: { keywords: ['curl', 'coil', 'defin', 'twist', 'wave'] } },
        { value: 'volume', label: 'Volume', phrase: 'volume', profile: { keywords: ['volume', 'thick', 'body', 'mousse', 'lift'] } },
        { value: 'hold', label: 'Strong hold', phrase: 'strong hold', profile: { keywords: ['hold', 'gel', 'freeze', 'max', 'strong', 'spritz'] } },
        { value: 'heat', label: 'Heat protection', phrase: 'heat protection', profile: { types: ['Heat Protectant'], keywords: ['heat', 'thermal', 'protect'] } }
      ]
    },

    wig_priority: {
      note: 'Got it.',
      title: 'What matters most?',
      cta: 'find_match',
      options: [
        { value: 'natural', label: 'Natural-looking everyday hair', phrase: 'natural-looking', profile: { keywords: ['natural', 'hd lace', 'glueless', 'lace front', 'human'] } },
        { value: 'length', label: 'Length', phrase: 'length', profile: { keywords: ['long', 'inch', '24', '26', '28', '30'] } },
        { value: 'volume', label: 'Volume', phrase: 'volume', profile: { keywords: ['body wave', 'curly', 'full', 'deep wave', 'bundle'] } },
        { value: 'protective', label: 'Protective styling', phrase: 'protective styling', profile: { keywords: ['braid', 'crochet', 'weave', 'bundle', 'twist'] } },
        { value: 'bold', label: 'Something bold', phrase: 'something bold', profile: { keywords: ['blonde', 'red', 'ombre', 'color', 'burgundy', 'highlight'] } },
        { value: 'easy', label: 'Easy installation', phrase: 'easy to wear', profile: { keywords: ['glueless', 'headband', 'wear and go', 'band', 'clip', 'drawstring'] } }
      ]
    },

    body_reset: {
      note: 'Good choice.',
      title: 'What kind of reset are we talking about?',
      cta: 'find_match',
      options: [
        {
          value: 'quick', label: 'Quick everyday care', phrase: 'everyday care',
          profile: { types: ['Body Lotion', 'Body Wash', 'Deodorant', 'Hand Care'], collections: [{ handle: finderCollections.bodyLotion, weight: 12, role: 'primary' }, { handle: finderCollections.bodyWash, weight: 8, role: 'primary' }], complementTypes: ['Body Wash', 'Deodorant'], searches: ['body lotion'] }
        },
        {
          value: 'soft', label: 'Soft, moisturized skin', phrase: 'soft skin',
          profile: { types: ['Body Cream', 'Body Lotion', 'Body Oil'], keywords: ['shea', 'cocoa', 'butter', 'moistur', 'nourish'], collections: [{ handle: finderCollections.bodyLotion, weight: 12, role: 'primary' }, { handle: finderCollections.bodyOils, weight: 10, role: 'primary' }], complementTypes: ['Body Wash', 'Body Scrub'], searches: ['shea body butter'] }
        },
        {
          value: 'scent', label: 'Smell amazing all day', phrase: 'a scent that lasts',
          profile: { types: ['Body Mist', 'Body Spray', 'Body Cream'], collections: [{ handle: finderCollections.bodyMist, weight: 14, role: 'primary' }], complementTypes: ['Body Lotion'], searches: ['body mist'] }
        },
        {
          value: 'spa', label: 'At-home spa night', phrase: 'a spa night',
          profile: { types: ['Body Scrub', 'Soap', 'Body Oil', 'Mask'], keywords: ['scrub', 'exfoliat', 'soap', 'bath', 'spa', 'salt'], collections: [{ handle: finderCollections.body, weight: 10, role: 'primary' }], complementTypes: ['Body Lotion', 'Body Oil'], searches: ['body scrub'] }
        },
        {
          value: 'full', label: 'Full body routine', phrase: 'a full body routine',
          profile: { types: ['Body Wash', 'Body Scrub', 'Body Lotion', 'Body Oil', 'Body Mist'], depth: 3, collections: [{ handle: finderCollections.body, weight: 10, role: 'primary' }], complementTypes: ['Body Mist', 'Body Scrub'] }
        },
        {
          value: 'surprise', label: 'Just show me something good', phrase: 'an open mind',
          profile: { collections: [{ handle: finderCollections.body, weight: 10, role: 'primary' }, { handle: finderCollections.bestSellers, weight: 8, role: 'discovery' }] }
        }
      ]
    },

    tools_group: {
      note: 'Good choice.',
      title: 'What are you upgrading?',
      cta: 'thats_me',
      options: [
        {
          value: 'makeup_tools', label: 'Makeup tools', phrase: 'makeup tools', branch: 'tools_makeup',
          profile: { types: ['Makeup Tools', 'Brush Sets'], collections: [{ handle: finderCollections.tools, weight: 12, role: 'primary' }], searches: ['makeup brush'] }
        },
        {
          value: 'hair_tools', label: 'Hair tools', phrase: 'hair tools', branch: 'tools_hair',
          profile: { types: ['Hair Tools', 'Wig Accessories'], collections: [{ handle: finderCollections.beautyTools, weight: 10, role: 'primary' }, { handle: finderCollections.tools, weight: 8, role: 'primary' }], searches: ['hair tools'] }
        },
        {
          value: 'nails', label: 'Nail essentials', phrase: 'nail essentials', branch: 'tools_nails',
          profile: { types: ['Nail Polish', 'Nail Care', 'Artificial Nails', 'Nail Tools'], collections: [{ handle: finderCollections.nails, weight: 14, role: 'primary' }], searches: ['nail polish'] }
        },
        {
          value: 'accessories', label: 'Beauty accessories', phrase: 'accessories', branch: null,
          profile: { types: ['Other Accessories', 'Other Beauty Tools', 'Sunglasses'], collections: [{ handle: finderCollections.accessories, weight: 14, role: 'primary' }], searches: ['beauty accessories'] }
        },
        {
          value: 'everyday', label: 'Everyday essentials', phrase: 'everyday essentials', branch: null,
          profile: { types: ['Other Beauty Tools', 'Makeup Tools', 'Other Accessories', 'Hand Care'], keywords: ['cap', 'bonnet', 'mirror', 'tweezer', 'headband', 'pad'], collections: [{ handle: finderCollections.beautyTools, weight: 10, role: 'primary' }, { handle: finderCollections.accessories, weight: 8, role: 'primary' }] }
        },
        {
          value: 'browsing', label: "I'm browsing", phrase: 'a browse', branch: null,
          profile: { collections: [{ handle: finderCollections.tools, weight: 10, role: 'primary' }, { handle: finderCollections.bestSellers, weight: 6, role: 'discovery' }] }
        }
      ]
    },

    tools_makeup: {
      note: 'Got it.',
      title: 'Which part of the kit?',
      cta: 'find_match',
      options: [
        { value: 'brushes', label: 'Brushes', phrase: 'brushes', profile: { keywords: ['brush'], types: ['Makeup Tools', 'Brush Sets'] } },
        { value: 'sponges', label: 'Sponges & blenders', phrase: 'blenders', profile: { keywords: ['sponge', 'blender', 'puff'] } },
        { value: 'lashes', label: 'Lashes & applicators', phrase: 'lash tools', profile: { keywords: ['lash', 'applicator', 'tweezer', 'curler'], types: ['Makeup Tools', 'False Lashes'] } },
        { value: 'full_set', label: 'A full set', phrase: 'a full set', profile: { keywords: ['set', 'kit', 'piece'], types: ['Brush Sets', 'Makeup Tools'] } }
      ]
    },

    tools_hair: {
      note: 'Got it.',
      title: 'What are you reaching for?',
      cta: 'find_match',
      options: [
        { value: 'heat', label: 'Heat styling', phrase: 'heat styling', profile: { keywords: ['flat iron', 'blow', 'dryer', 'curl', 'straight', 'wand'] } },
        { value: 'brushes', label: 'Brushes & combs', phrase: 'brushes and combs', profile: { keywords: ['brush', 'comb', 'paddle', 'detangl'] } },
        { value: 'clippers', label: 'Clippers & trimmers', phrase: 'clippers', profile: { keywords: ['clipper', 'trimmer', 'shaver', 'razor'] } },
        { value: 'wig_care', label: 'Wig & install essentials', phrase: 'install essentials', profile: { keywords: ['cap', 'glue', 'lace', 'band', 'bonnet', 'spray'], types: ['Wig Accessories'] } }
      ]
    },

    tools_nails: {
      note: 'Got it.',
      title: 'What are you after?',
      cta: 'find_match',
      options: [
        { value: 'press_on', label: 'Press-ons', phrase: 'press-ons', profile: { types: ['Artificial Nails'], keywords: ['press', 'glue on', 'tip', 'nail'] } },
        { value: 'polish', label: 'Polish', phrase: 'polish', profile: { types: ['Nail Polish'], keywords: ['polish', 'lacquer', 'gel'] } },
        { value: 'care', label: 'Nail care & tools', phrase: 'nail care', profile: { types: ['Nail Care', 'Nail Tools'], keywords: ['cuticle', 'file', 'buffer', 'clipper', 'treatment'] } }
      ]
    },

    gift_recipient: {
      note: 'Nice — let’s make it easy.',
      title: 'Who are we shopping for?',
      cta: 'continue',
      options: [
        { value: 'her', label: 'Her', phrase: 'a gift for her', profile: { gender: 'Women' } },
        { value: 'him', label: 'Him', phrase: 'a gift for him', profile: { gender: 'Men' } },
        { value: 'teen', label: 'Teen', phrase: 'a gift for a teen', profile: { keywords: ['mini', 'set', 'gloss', 'mist'], collections: [{ handle: finderCollections.miniSizes, weight: 10, role: 'discovery' }] } },
        { value: 'friend', label: 'Friend', phrase: 'a gift for a friend', profile: { collections: [{ handle: finderCollections.giftSets, weight: 10, role: 'discovery' }] } },
        { value: 'partner', label: 'Partner', phrase: 'a gift for your partner', profile: { keywords: ['intense', 'noir', 'velvet', 'gold'] } },
        { value: 'family', label: 'Family', phrase: 'a gift for family', profile: { collections: [{ handle: finderCollections.valueSets, weight: 10, role: 'discovery' }] } },
        { value: 'not_sure', label: 'Not sure', phrase: 'a gift', profile: {} }
      ]
    },

    gift_type: {
      note: "Let's narrow it down.",
      title: 'What kind of gift are you thinking?',
      cta: 'next_question',
      options: [
        {
          value: 'fragrance', label: 'Fragrance', phrase: 'fragrance',
          profile: { types: FINDER_FRAGRANCE_TYPES, collections: [{ handle: finderCollections.perfumes, weight: 12, role: 'primary' }, { handle: finderCollections.giftSets, weight: 8, role: 'complement' }], searches: ['perfume gift set'] }
        },
        {
          value: 'makeup', label: 'Makeup', phrase: 'makeup',
          profile: { types: ['Makeup Palette', 'Lip Gloss', 'Mascara', 'Brush Sets', 'Lipstick', 'Highlighter'], collections: [{ handle: finderCollections.makeup, weight: 10, role: 'primary' }, { handle: finderCollections.valueSets, weight: 8, role: 'complement' }], searches: ['makeup set'] }
        },
        {
          value: 'skincare', label: 'Skincare', phrase: 'skincare',
          profile: { types: ['Cleanser', 'Serum', 'Moisturizer', 'Mask', 'Other Skincare'], collections: [{ handle: finderCollections.skincare, weight: 12, role: 'primary' }], searches: ['skincare set'] }
        },
        {
          value: 'hair', label: 'Hair', phrase: 'haircare',
          profile: { types: ['Shampoo', 'Conditioner', 'Hair Mask', 'Hair Oil', 'Treatment'], collections: [{ handle: finderCollections.haircare, weight: 10, role: 'primary' }, { handle: finderCollections.hairOils, weight: 8, role: 'complement' }], searches: ['hair care set'] }
        },
        {
          value: 'self_care', label: 'Self-care', phrase: 'self-care',
          profile: { types: ['Body Cream', 'Body Lotion', 'Body Oil', 'Body Scrub', 'Soap', 'Body Mist'], collections: [{ handle: finderCollections.body, weight: 12, role: 'primary' }, { handle: finderCollections.giftSets, weight: 8, role: 'complement' }], searches: ['body gift set'] }
        },
        {
          value: 'everything', label: 'A little bit of everything', phrase: 'a bit of everything',
          profile: { collections: [{ handle: finderCollections.giftSets, weight: 14, role: 'primary' }, { handle: finderCollections.valueSets, weight: 12, role: 'primary' }, { handle: finderCollections.miniSizes, weight: 8, role: 'discovery' }], searches: ['gift set'] }
        },
        {
          value: 'help_me', label: 'Help me choose', phrase: 'an open mind',
          profile: { collections: [{ handle: finderCollections.giftSets, weight: 12, role: 'primary' }, { handle: finderCollections.bestSellers, weight: 10, role: 'discovery' }, { handle: finderCollections.valueSets, weight: 8, role: 'primary' }] }
        }
      ]
    },

    budget: {
      note: 'Last one.',
      title: 'How much are we treating ourselves?',
      cta: 'show_edit',
      options: []
    },

    gift_budget: {
      note: 'Last one.',
      title: "What's your gift budget?",
      cta: 'show_edit',
      options: []
    }
  };

  finderQuestions.budget.options = finderBudgetOptions(false);
  finderQuestions.gift_budget.options = finderBudgetOptions(true);

  /* ---- Flow ---------------------------------------------------------------
     Which question comes next depends on what has been answered, so the flow
     is recomputed after every answer rather than being a fixed list. */

  function finderOptionFor(state, questionId) {
    var answer = state.answers[questionId];
    if (!answer) return null;
    var question = finderQuestions[questionId];
    if (!question) return null;
    for (var i = 0; i < question.options.length; i += 1) {
      if (question.options[i].value === answer) return question.options[i];
    }
    return null;
  }

  function finderOpenCount(state, ids) {
    var count = 0;
    for (var i = 0; i < ids.length; i += 1) {
      var value = state.answers[ids[i]];
      if (value && FINDER_OPEN_ANSWERS.indexOf(value) > -1) count += 1;
    }
    return count;
  }

  function finderFlow(state) {
    var ids = ['intent'];
    var intent = finderOptionFor(state, 'intent');
    if (!intent) return { ids: ids, estimate: 5 };

    if (intent.branch === 'gift') {
      ids.push('gift_recipient');
      if (state.answers.gift_recipient) ids.push('gift_type');
      if (state.answers.gift_type) ids.push('gift_budget');
      return { ids: ids, estimate: 4 };
    }

    ids.push('category');
    var category = finderOptionFor(state, 'category');
    if (!category) return { ids: ids, estimate: 5 };

    var branch = category.branch;
    var estimate = 5;

    if (branch === 'fragrance') {
      ids.push('frag_vibe');
      var vibe = finderOptionFor(state, 'frag_vibe');
      if (vibe) ids.push('frag_occasion');
      var occasion = finderOptionFor(state, 'frag_occasion');
      /* Only ask for a family when the vibe question did not already give one. */
      if (occasion && vibe && vibe.value === 'surprise') ids.push('frag_family');
      estimate = 5;
    } else if (branch === 'makeup') {
      ids.push('makeup_look');
      var look = finderOptionFor(state, 'makeup_look');
      if (look) ids.push('makeup_priority');
      if (look && state.answers.makeup_priority) {
        if (look.value === 'lips') ids.push('makeup_lip_shade');
        else if (look.value === 'no_makeup' || look.value === 'everyday' || look.value === 'full_glam') ids.push('makeup_finish');
      }
      estimate = 6;
    } else if (branch === 'skincare') {
      ids.push('skin_need');
      if (state.answers.skin_need) ids.push('skin_routine');
      estimate = 5;
    } else if (branch === 'hair') {
      ids.push('hair_need');
      var need = finderOptionFor(state, 'hair_need');
      if (need && need.branch) ids.push(need.branch);
      estimate = 5;
    } else if (branch === 'body') {
      ids.push('body_reset');
      estimate = 4;
    } else if (branch === 'tools') {
      ids.push('tools_group');
      var group = finderOptionFor(state, 'tools_group');
      if (group && group.branch) ids.push(group.branch);
      estimate = 5;
    }

    /* Two "I don't know" answers is an answer in itself: stop asking for
       detail and go straight to budget. */
    if (finderOpenCount(state, ids) >= 2) {
      var trimmed = [];
      for (var i = 0; i < ids.length; i += 1) {
        if (!state.answers[ids[i]]) break;
        trimmed.push(ids[i]);
      }
      ids = trimmed;
      state.curated = true;
    } else {
      state.curated = false;
    }

    ids.push('budget');
    return { ids: ids, estimate: Math.max(estimate, ids.length) };
  }

  /* ---- Profile ------------------------------------------------------------ */

  function finderMergeProfile(profile, extra) {
    if (!extra) return profile;
    ['types', 'keywords', 'vibe', 'complementTypes', 'searches', 'concentration'].forEach(function (key) {
      if (!extra[key]) return;
      for (var i = 0; i < extra[key].length; i += 1) {
        if (profile[key].indexOf(extra[key][i]) === -1) profile[key].push(extra[key][i]);
      }
    });

    if (extra.collections) {
      extra.collections.forEach(function (entry) {
        var existing = profile.collections.filter(function (item) {
          return item.handle === entry.handle;
        })[0];
        if (existing) {
          existing.weight = Math.max(existing.weight, entry.weight);
          if (entry.role === 'primary') existing.role = 'primary';
        } else {
          profile.collections.push({ handle: entry.handle, weight: entry.weight, role: entry.role });
        }
      });
    }

    if (extra.gift) profile.gift = true;
    if (extra.gender) profile.gender = extra.gender;
    if (extra.area) profile.area = extra.area;
    if (extra.depth) profile.depth = Math.max(profile.depth, extra.depth);
    return profile;
  }

  function finderBuildProfile(state, flow) {
    var profile = {
      types: [],
      focusTypes: [],
      keywords: [],
      vibe: [],
      complementTypes: [],
      searches: [],
      concentration: [],
      collections: [],
      gift: false,
      gender: null,
      area: null,
      depth: 2,
      budget: finderBudgets.open,
      phrases: [],
      chips: [],
      branch: null,
      curated: !!state.curated
    };

    flow.ids.forEach(function (id) {
      var option = finderOptionFor(state, id);
      if (!option) return;
      if (id === 'category') profile.branch = option.branch;
      if (id === 'intent' && option.branch === 'gift') profile.branch = 'gift';

      if (id === 'budget' || id === 'gift_budget') {
        profile.budget = finderBudgets[option.value] || finderBudgets.open;
        profile.phrases.push(profile.budget.phrase);
        profile.chips.push(option.label);
        return;
      }

      finderMergeProfile(profile, option.profile);
      if (FINDER_FOCUS_QUESTIONS.indexOf(id) > -1 && option.profile && option.profile.types) {
        option.profile.types.forEach(function (type) {
          if (profile.focusTypes.indexOf(type) === -1) profile.focusTypes.push(type);
        });
      }
      if (id !== 'intent' && option.phrase && profile.phrases.indexOf(option.phrase) === -1 && option.phrase !== 'an open mind') {
        profile.phrases.push(option.phrase);
      }
      profile.chips.push(option.label);
    });

    if (profile.gift) {
      profile.penaltyTypes = FINDER_SHADE_CRITICAL;
      profile.giftKeywords = ['set', 'gift', 'kit', 'collection', 'bundle', 'duo', 'trio'];
      if (profile.collections.length === 0) {
        profile.collections.push({ handle: finderCollections.giftSets, weight: 12, role: 'primary' });
        profile.collections.push({ handle: finderCollections.valueSets, weight: 10, role: 'primary' });
      }
    } else {
      profile.penaltyTypes = [];
      profile.giftKeywords = [];
    }

    /* When the answers said "I don't know", the shop's own favourites are the
       most honest thing to lead with. */
    if (profile.curated) {
      finderMergeProfile(profile, {
        collections: [
          { handle: finderCollections.bestSellers, weight: 9, role: 'complement' },
          { handle: finderCollections.newIn, weight: 7, role: 'complement' }
        ]
      });
    }

    if (profile.collections.length === 0) {
      profile.collections.push({ handle: finderCollections.bestSellers, weight: 8, role: 'primary' });
    }

    return profile;
  }

  /* ---- Feeds --------------------------------------------------------------
     Candidates come from the storefront itself: the collections that match the
     answers, plus Shopify's own search ranking for the words the answers
     imply. Nothing is fabricated locally. */

  var finderFeedCache = {};

  function finderFetchFeed(url) {
    if (finderFeedCache[url]) return finderFeedCache[url];

    var request = fetch(url, { headers: { Accept: 'application/json, text/plain, */*' } })
      .then(function (response) {
        if (!response.ok) throw new Error('feed');
        return response.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        return data && data.items ? data.items : [];
      })
      .catch(function () {
        return [];
      });

    finderFeedCache[url] = request;
    return request;
  }

  function finderCollectionUrl(root, handle, page) {
    var base = root.getAttribute('data-finder-collection-route') || '/collections';
    return base + '/' + handle + '?view=finder' + (page && page > 1 ? '&page=' + page : '');
  }

  function finderSearchUrl(root, terms) {
    var base = root.getAttribute('data-finder-search-route') || '/search';
    return base + '?q=' + encodeURIComponent(terms) + '&type=product&view=finder&options[prefix]=last';
  }

  function finderRequests(root, profile) {
    var requests = [];

    profile.collections
      .slice()
      .sort(function (a, b) {
        return b.weight - a.weight;
      })
      .slice(0, 4)
      .forEach(function (entry) {
        requests.push({ url: finderCollectionUrl(root, entry.handle), weight: entry.weight, role: entry.role, handle: entry.handle });
      });

    profile.searches.slice(0, 2).forEach(function (terms) {
      requests.push({ url: finderSearchUrl(root, terms), weight: 8, role: 'search', handle: 'search:' + terms });
    });

    return requests;
  }

  function finderGather(root, profile) {
    var requests = finderRequests(root, profile);
    var pool = {};

    function absorb(request, items) {
      items.forEach(function (item, index) {
        if (!item || !item.id) return;
        var existing = pool[item.id];
        var provenance = { weight: request.weight, role: request.role, rank: index };
        if (existing) {
          existing.sources.push(provenance);
          if (index < existing.bestRank) existing.bestRank = index;
          if (request.weight > existing.bestWeight) existing.bestWeight = request.weight;
        } else {
          item.sources = [provenance];
          item.bestRank = index;
          item.bestWeight = request.weight;
          pool[item.id] = item;
        }
      });
    }

    return Promise.all(
      requests.map(function (request) {
        return finderFetchFeed(request.url).then(function (items) {
          absorb(request, items);
        });
      })
    ).then(function () {
      var list = finderPoolToList(pool);
      if (list.length >= 12) return list;

      /* Thin path — widen with the fallback collection rather than showing a
         short, sad edit. */
      var fallback = root.getAttribute('data-finder-all-collection') || 'best-sellers';
      var extra = { url: finderCollectionUrl(root, fallback), weight: 4, role: 'fallback', handle: fallback };
      return finderFetchFeed(extra.url).then(function (items) {
        absorb(extra, items);
        return finderPoolToList(pool);
      });
    });
  }

  function finderPoolToList(pool) {
    var list = [];
    Object.keys(pool).forEach(function (key) {
      list.push(pool[key]);
    });
    return list;
  }

  /* ---- Scoring ------------------------------------------------------------
     Category and stated need carry the most weight, vibe and budget less,
     provenance and popularity least. Availability and a usable image are
     requirements, not weights. */

  function finderHaystack(item) {
    if (!item.__haystack) {
      item.__haystack = ((item.title || '') + ' ' + (item.vendor || '') + ' ' + (item.type || '')).toLowerCase();
    }
    return item.__haystack;
  }

  function finderHits(item, words, cap, per) {
    if (!words || !words.length) return 0;
    var hay = finderHaystack(item);
    var hits = 0;
    for (var i = 0; i < words.length; i += 1) {
      if (hay.indexOf(words[i]) > -1) hits += 1;
      if (hits * per >= cap) break;
    }
    return Math.min(cap, hits * per);
  }

  function finderBudgetScore(profile, price) {
    var budget = profile.budget;
    if (!budget || (budget.min === null && budget.max === null)) return 6;
    if (typeof price !== 'number') return 0;

    var min = budget.min === null ? 0 : budget.min;
    var max = budget.max === null ? Infinity : budget.max;
    if (price >= min && price <= max) return 18;

    /* Just outside the band is still worth showing; far outside is not. */
    var distance = price < min ? min - price : price - max;
    if (distance <= 1500) return 8;
    if (distance <= 4000) return -6;
    return -18;
  }

  function finderScore(item, profile) {
    var score = 0;

    if (profile.focusTypes.length && item.type && profile.focusTypes.indexOf(item.type) > -1) score += 22;

    if (profile.types.length && item.type) {
      if (profile.types.indexOf(item.type) > -1) score += 40;
      else if (profile.complementTypes.indexOf(item.type) > -1) score += 14;
      else score -= 6;
    }

    score += finderHits(item, profile.keywords, 30, 10);
    score += finderHits(item, profile.vibe, 18, 6);
    score += finderBudgetScore(profile, item.price);

    if (profile.concentration.length && item.concentration) {
      var index = profile.concentration.indexOf(item.concentration);
      if (index === 0) score += 10;
      else if (index > 0) score += 5;
    }

    if (profile.area && item.area) {
      score += item.area === profile.area ? 8 : -4;
    }

    if (profile.gender && item.gender) {
      if (item.gender === profile.gender) score += 12;
      else if (item.gender === 'Unisex') score += 4;
      else score -= 10;
    } else if (profile.gender) {
      /* No gender metafield on this product. The title is the only honest
         signal left, and only when it states an audience outright. */
      var stated = finderStatedAudience(item);
      if (stated) score += stated === profile.gender ? 8 : -12;
    }

    if (profile.gift) {
      score += finderHits(item, profile.giftKeywords, 12, 6);
      if (item.type && profile.penaltyTypes.indexOf(item.type) > -1) score -= 20;
    }

    /* Provenance: a product the merchandised collection already puts near the
       top is a safer recommendation than one dug out of page three. */
    score += Math.min(12, item.bestWeight || 0);
    score += Math.max(0, 10 - (item.bestRank || 0) * 0.4);
    if (item.sources && item.sources.length > 1) score += 6;

    if (item.compareAtPrice && item.price && item.compareAtPrice > item.price) score += 3;

    score += finderCrossAreaPenalty(item, profile);

    return score;
  }

  /* Some products sit in a collection that does not match what they are for —
     a hair serum filed under skincare, say. The finder will not invent a
     category Shopify does not hold, but it can read the words the product's
     own title uses and stop leading with something meant for somewhere else.
     A penalty, not a filter: if the catalogue has nothing better, it still
     shows. */
  var FINDER_CROSS_AREA_WORDS = {
    skincare: ['hair', 'scalp', 'lengths', 'curl', 'braid', 'wig', 'shampoo', 'conditioner', 'edge control', 'relaxer', 'pomade', 'growth', 'thermal', 'strengthening', 'detangl'],
    hair: ['facial', 'face wash', 'face cream'],
    makeup: ['shampoo', 'conditioner', 'body wash'],
    fragrance: []
  };

  function finderCrossAreaPenalty(item, profile) {
    var words = FINDER_CROSS_AREA_WORDS[profile.branch];
    if (!words || !words.length) return 0;
    var hay = finderHaystack(item);
    for (var i = 0; i < words.length; i += 1) {
      if (hay.indexOf(words[i]) > -1) return -25;
    }
    return 0;
  }

  function finderFromCategoryFeed(item) {
    if (!item.sources) return false;
    for (var i = 0; i < item.sources.length; i += 1) {
      var role = item.sources[i].role;
      if (role === 'primary' || role === 'complement') return true;
    }
    return false;
  }

  var FINDER_AUDIENCE_MARKERS = [
    { gender: 'Women', words: [' for women', ' for her', ' pour femme', ' femme', ' miss ', " women's"] },
    { gender: 'Men', words: [' for men', ' for him', ' pour homme', ' homme', ' mens ', " men's"] }
  ];

  function finderStatedAudience(item) {
    var hay = ' ' + finderHaystack(item) + ' ';
    for (var i = 0; i < FINDER_AUDIENCE_MARKERS.length; i += 1) {
      var marker = FINDER_AUDIENCE_MARKERS[i];
      for (var w = 0; w < marker.words.length; w += 1) {
        if (hay.indexOf(marker.words[w]) > -1) return marker.gender;
      }
    }
    return null;
  }

  function finderEligible(item) {
    return !!(item && item.available && item.image && item.url);
  }

  /* ---- Slotting -----------------------------------------------------------
     Six to twelve products, grouped the way an advisor would hand them over:
     the one they would lead with, a few alongside it, what finishes the
     routine, and something worth a look. */

  function finderSlotCount(slots) {
    return (slots.best ? 1 : 0) + slots.also.length + slots.complement.length + slots.worth.length;
  }

  /* Relevance first, but an edit of two products is not an edit. The gate is
     relaxed one step at a time — strict, then category only, then open —
     rather than dropped, so a thin path is filled with the next most relevant
     thing rather than with whatever happened to score. */
  /* Gate first, then the diversity caps. A catalogue where one vendor owns a
     whole category should still produce a full edit. */
  var FINDER_PASSES = [
    { gate: 2, vendorCap: 2, typeCap: 3 },
    { gate: 2, vendorCap: 3, typeCap: 4 },
    { gate: 1, vendorCap: 3, typeCap: 4 },
    { gate: 1, vendorCap: 4, typeCap: 6 },
    { gate: 1, vendorCap: 0, typeCap: 0 },
    { gate: 0, vendorCap: 0, typeCap: 0 }
  ];

  function finderSlots(list, profile) {
    var best = null;
    var bestCount = -1;
    for (var i = 0; i < FINDER_PASSES.length; i += 1) {
      var pass = FINDER_PASSES[i];
      var slots = finderFillSlots(list, profile, pass.gate, pass.vendorCap, pass.typeCap);
      var count = finderSlotCount(slots);
      if (count > bestCount) {
        best = slots;
        bestCount = count;
      }
      if (count >= 6) return slots;
    }
    return best;
  }

  function finderFillSlots(list, profile, gate, vendorCap, typeCap) {
    var scored = list
      .filter(finderEligible)
      .map(function (item) {
        item.score = finderScore(item, profile);
        return item;
      })
      .sort(function (a, b) {
        return b.score - a.score;
      });

    /* Leftovers are not recommendations. A product only stays in the edit if it
       belongs to what was actually asked for — the wider discovery feeds are
       there to deepen a thin path, not to pad a full one with another
       category. The gate lifts only when honouring it would leave too little
       to show. */
    var lead = scored.length ? scored[0].score : 0;
    var relevant = scored.filter(function (item) {
      if (gate > 1 && item.score < Math.max(20, lead * 0.4)) return false;
      if (gate > 1 && FINDER_AMBIGUOUS_TYPES.indexOf(item.type) > -1 && !finderFromCategoryFeed(item)) return false;
      if (!profile.types.length) return true;
      return profile.types.indexOf(item.type) > -1 || profile.complementTypes.indexOf(item.type) > -1;
    });
    if (gate > 0 && relevant.length >= 6) scored = relevant;

    var used = {};
    var vendorCount = {};
    var typeCount = {};
    var titleKeys = {};

    /* The catalogue lists sizes and shades as separate products, so two cards
       can be different products and still read as the same one. */
    function titleKey(item) {
      /* Numbers are what separates one shade, size or brush of a series from
         the next, so they are dropped: eight numbered brushes from one range
         are one recommendation, not eight. */
      return (item.title || '')
        .toLowerCase()
        .replace(/[^a-z]+/g, ' ')
        .trim()
        .slice(0, 26);
    }

    function take(item, vendorCap, typeCap) {
      if (!item || used[item.id]) return false;
      var vendor = item.vendor || '';
      var type = item.type || '';
      var key = titleKey(item);
      if (key && titleKeys[key]) return false;
      if (vendorCap && (vendorCount[vendor] || 0) >= vendorCap) return false;
      if (typeCap && (typeCount[type] || 0) >= typeCap) return false;
      used[item.id] = true;
      titleKeys[key] = true;
      vendorCount[vendor] = (vendorCount[vendor] || 0) + 1;
      typeCount[type] = (typeCount[type] || 0) + 1;
      return true;
    }

    var best = null;
    for (var i = 0; i < scored.length; i += 1) {
      if (take(scored[i], 0, 0)) {
        best = scored[i];
        break;
      }
    }

    var complementTarget = profile.depth === 1 ? 2 : 3;
    var complement = [];
    if (profile.complementTypes.length) {
      for (var c = 0; c < scored.length && complement.length < complementTarget; c += 1) {
        var candidate = scored[c];
        if (used[candidate.id]) continue;
        if (profile.complementTypes.indexOf(candidate.type) === -1) continue;
        if (best && candidate.type === best.type) continue;
        if (take(candidate, vendorCap, vendorCap ? 2 : 0)) complement.push(candidate);
      }
    }

    var alsoTarget = profile.depth >= 3 ? 5 : profile.depth === 1 ? 3 : 4;
    var also = [];
    for (var a = 0; a < scored.length && also.length < alsoTarget; a += 1) {
      if (take(scored[a], vendorCap, typeCap)) also.push(scored[a]);
    }

    var worth = [];
    for (var w = 0; w < scored.length && worth.length < 3; w += 1) {
      var pick = scored[w];
      if (used[pick.id]) continue;
      if (vendorCap && (vendorCount[pick.vendor || ''] || 0) >= vendorCap) continue;
      if (take(pick, vendorCap, typeCap)) worth.push(pick);
    }

    return { best: best, also: also, complement: complement, worth: worth };
  }

  /* ---- Copy --------------------------------------------------------------- */

  var finderClosings = {
    fragrance: "we think you'll like these.",
    makeup: "here's where we'd start.",
    skincare: 'start here.',
    hair: "this is where we'd start.",
    body: 'these are the ones we reach for.',
    tools: 'these earn their place.',
    gift: 'these gift well.'
  };

  function finderSummaryText(profile) {
    /* The budget is the line's closing beat, so it is kept whole and the
       answer phrases before it are trimmed instead. */
    var budgetPhrase = profile.budget ? profile.budget.phrase : null;
    var phrases = profile.phrases
      .filter(function (phrase) {
        return phrase !== budgetPhrase;
      })
      .slice(0, 2);
    if (budgetPhrase) phrases.push(budgetPhrase);
    var closing = finderClosings[profile.branch] || "here's where we'd start.";

    if (profile.curated || phrases.length === 0) {
      return 'Not sure yet, and that is fine — these are the ones people keep coming back to.';
    }

    var joined;
    if (phrases.length === 1) joined = phrases[0];
    else joined = phrases.slice(0, phrases.length - 1).join(', ') + ' and ' + phrases[phrases.length - 1];

    var sentence = joined.charAt(0).toUpperCase() + joined.slice(1);
    return sentence + ' — ' + closing;
  }

  function finderReason(item, profile) {
    if (profile.types.length && item.type && profile.types.indexOf(item.type) > -1) return item.type;
    if (profile.complementTypes.indexOf(item.type) > -1) return 'Rounds out the routine';
    if (item.type) return item.type;
    return '';
  }

  /* ---- Rendering ---------------------------------------------------------- */

  function finderMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (window.Y10 && window.Y10.moneyFormat) return formatMoney(cents, window.Y10.moneyFormat);
    return '$' + (cents / 100).toFixed(2);
  }

  function finderCard(parts, item, profile) {
    var node = parts.cardTemplate.content.firstElementChild.cloneNode(true);
    var link = node.querySelector('[data-card-link]');
    var image = node.querySelector('[data-card-image]');
    var vendor = node.querySelector('[data-card-vendor]');
    var title = node.querySelector('[data-card-title]');
    var reason = node.querySelector('[data-card-reason]');
    var price = node.querySelector('[data-card-price]');
    var compare = node.querySelector('[data-card-compare]');
    var action = node.querySelector('[data-card-action]');
    var wishlist = node.querySelector('[data-wishlist-button]');

    if (link) {
      link.href = item.url;
      link.setAttribute('aria-label', item.title);
    }
    if (image) {
      image.src = item.image;
      image.alt = item.imageAlt || item.title;
      if (item.imageHover) image.setAttribute('data-hover', item.imageHover);
    }
    if (vendor) {
      if (item.vendor) vendor.textContent = item.vendor;
      else vendor.remove();
    }
    if (title) {
      title.href = item.url;
      title.textContent = item.title;
    }
    if (reason) {
      var reasonText = finderReason(item, profile);
      if (reasonText) reason.textContent = reasonText;
      else reason.remove();
    }
    if (price) price.textContent = finderMoney(item.price);
    if (compare) {
      if (item.compareAtPrice && item.price && item.compareAtPrice > item.price) {
        compare.textContent = finderMoney(item.compareAtPrice);
        compare.hidden = false;
        node.querySelector('.price').classList.add('price--on-sale');
      } else {
        compare.remove();
      }
    }

    if (wishlist) {
      wishlist.setAttribute('data-product-handle', item.handle);
      ['data-wishlist-add-label', 'data-wishlist-remove-label'].forEach(function (attribute) {
        var value = wishlist.getAttribute(attribute) || '';
        wishlist.setAttribute(attribute, value.replace('PRODUCT_TITLE', item.title));
      });
      wishlist.setAttribute('aria-label', wishlist.getAttribute('data-wishlist-add-label') || '');
    }

    if (action) {
      /* A card never picks a variant on the customer's behalf — same rule the
         Liquid quick-add follows. */
      if (item.singleVariant === true && item.variantId) {
        var addNode = parts.addTemplate.content.firstElementChild.cloneNode(true);
        var variantField = addNode.querySelector('[data-card-variant]');
        if (variantField) variantField.value = item.variantId;
        action.appendChild(addNode);
      } else {
        var chooseNode = parts.chooseTemplate.content.firstElementChild.cloneNode(true);
        var chooseLink = chooseNode.querySelector('[data-card-link]');
        if (chooseLink) chooseLink.href = item.url;
        action.appendChild(chooseNode);
      }
    }

    return node;
  }

  function finderGroup(parts, heading, items, profile, variant) {
    if (!items || !items.length) return null;

    var group = document.createElement('section');
    group.className = 'finder-group finder-group--' + variant;

    var title = document.createElement('h3');
    title.className = 'finder-group__heading';
    title.textContent = heading;
    group.appendChild(title);

    var grid = document.createElement('div');
    grid.className = 'finder-group__grid';
    items.forEach(function (item) {
      grid.appendChild(finderCard(parts, item, profile));
    });
    group.appendChild(grid);

    return group;
  }

  function finderSaveHistory(profile, slots) {
    var store = window.Y10 && window.Y10.beautyHistory;
    if (!store) return;

    var items = []
      .concat(slots.best ? [slots.best] : [], slots.also || [], slots.complement || [], slots.worth || [])
      .filter(Boolean);

    var branchLabels = {
      fragrance: 'Fragrance', makeup: 'Makeup', skincare: 'Skincare',
      hair: 'Haircare', body: 'Body', tools: 'Tools & Brushes', gift: 'Gifting'
    };
    var category = branchLabels[profile.branch] || 'Beauty';
    var title = category + ' Edit';

    store.add({
      id: 'edit-' + Date.now(),
      date: new Date().toISOString(),
      category: category,
      title: title,
      answers: (profile.chips || []).slice(0, 6),
      url: window.location.pathname + window.location.search,
      products: items.slice(0, 8).map(function (item) {
        return { title: item.title, url: item.url, image: item.image };
      })
    });
  }

  function finderRenderEdit(root, parts, profile, slots) {
    var groups = parts.groups;
    groups.textContent = '';

    var completeHeading = profile.branch === 'makeup' ? parts.strings.completeLook : parts.strings.completeRoutine;
    var blocks = [
      finderGroup(parts, parts.strings.bestMatch, slots.best ? [slots.best] : [], profile, 'best'),
      finderGroup(parts, parts.strings.also, slots.also, profile, 'also'),
      finderGroup(parts, completeHeading, slots.complement, profile, 'complete'),
      finderGroup(parts, parts.strings.worthLook, slots.worth, profile, 'worth')
    ];

    var rendered = 0;
    blocks.forEach(function (block) {
      if (!block) return;
      groups.appendChild(block);
      rendered += 1;
    });

    parts.summary.textContent = finderSummaryText(profile);

    parts.chips.textContent = '';
    profile.chips.forEach(function (label) {
      var chip = document.createElement('li');
      chip.className = 'finder-chip';
      chip.textContent = label;
      parts.chips.appendChild(chip);
    });

    var primary = profile.collections.slice().sort(function (a, b) {
      return b.weight - a.weight;
    })[0];
    if (primary && parts.shop) {
      parts.shop.href = (root.getAttribute('data-finder-collection-route') || '/collections') + '/' + primary.handle;
    }

    parts.empty.hidden = rendered > 0;
    parts.stage.hidden = true;
    parts.loading.hidden = true;
    parts.edit.hidden = false;

    finderSaveHistory(profile, slots);

    updateWishlistButtons(parts.edit);
    parts.edit.setAttribute('tabindex', '-1');
    parts.edit.focus({ preventScroll: true });
    parts.edit.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function finderRenderProgress(parts, flow, index) {
    var labels = parts.strings.progress || [];
    var total = Math.max(flow.estimate, flow.ids.length);
    /* The last label belongs to the edit itself, so the questions spread across
       the ones before it and the indicator moves on every answer. */
    var phase = Math.min(labels.length - 2, Math.floor(((index + 1) * (labels.length - 1)) / Math.max(1, total)));

    parts.progress.textContent = '';
    labels.forEach(function (label, position) {
      var step = document.createElement('span');
      step.className = 'finder-progress__step';
      if (position === phase) step.classList.add('is-active');
      if (position < phase) step.classList.add('is-done');
      step.textContent = '0' + (position + 1) + ' — ' + label;
      parts.progress.appendChild(step);
    });
  }

  function finderRenderQuestion(root, parts, state) {
    var flow = finderFlow(state);
    var questionId = flow.ids[Math.min(state.step, flow.ids.length - 1)];
    var question = finderQuestions[questionId];
    if (!question) return;

    state.currentId = questionId;

    finderRenderProgress(parts, flow, state.step);

    parts.note.textContent = question.note || '';
    parts.question.textContent = question.title;
    parts.options.textContent = '';
    parts.options.setAttribute('aria-label', question.title);

    question.options.forEach(function (option) {
      var node = parts.optionTemplate.content.firstElementChild.cloneNode(true);
      var icon = node.querySelector('[data-option-icon]');
      var label = node.querySelector('[data-option-label]');

      node.setAttribute('data-value', option.value);
      if (label) label.textContent = option.label;
      if (icon) {
        if (option.icon && parts.icons[option.icon]) icon.innerHTML = parts.icons[option.icon];
        else icon.remove();
      }
      if (state.answers[questionId] === option.value) {
        node.classList.add('is-selected');
        node.setAttribute('aria-checked', 'true');
      }
      parts.options.appendChild(node);
    });

    parts.nextLabel.textContent = parts.strings[question.cta] || parts.strings.continue;
    parts.next.disabled = !state.answers[questionId];
    parts.back.hidden = state.step === 0;
    parts.stage.hidden = false;
    parts.edit.hidden = true;
    parts.loading.hidden = true;
    parts.question.focus({ preventScroll: true });
  }

  function finderBuildEdit(root, parts, state) {
    var flow = finderFlow(state);
    var profile = finderBuildProfile(state, flow);

    parts.stage.hidden = true;
    parts.loading.hidden = false;

    finderGather(root, profile).then(function (list) {
      var slots = finderSlots(list, profile);
      if (!slots.best) {
        parts.loading.hidden = true;
        parts.edit.hidden = false;
        parts.groups.textContent = '';
        parts.summary.textContent = finderSummaryText(profile);
        parts.empty.hidden = false;
        return;
      }
      finderRenderEdit(root, parts, profile, slots);
    });
  }

  function finderSetup(root) {
    var stringsNode = root.querySelector('[data-finder-strings]');
    var strings = {};
    if (stringsNode) {
      try {
        strings = JSON.parse(stringsNode.textContent);
      } catch (error) {
        strings = {};
      }
    }

    var parts = {
      stage: root.querySelector('[data-finder-stage]'),
      progress: root.querySelector('[data-finder-progress]'),
      note: root.querySelector('[data-finder-note]'),
      question: root.querySelector('[data-finder-question]'),
      options: root.querySelector('[data-finder-options]'),
      next: root.querySelector('[data-finder-next]'),
      nextLabel: root.querySelector('[data-finder-next-label]'),
      back: root.querySelector('[data-finder-back]'),
      loading: root.querySelector('[data-finder-loading]'),
      edit: root.querySelector('[data-finder-edit]'),
      groups: root.querySelector('[data-finder-groups]'),
      summary: root.querySelector('[data-finder-summary]'),
      chips: root.querySelector('[data-finder-chips]'),
      empty: root.querySelector('[data-finder-empty]'),
      shop: root.querySelector('[data-finder-shop]'),
      restart: root.querySelector('[data-finder-restart]'),
      cardTemplate: root.querySelector('[data-finder-card-template]'),
      addTemplate: root.querySelector('[data-finder-add-template]'),
      chooseTemplate: root.querySelector('[data-finder-choose-template]'),
      optionTemplate: root.querySelector('[data-finder-option-template]'),
      strings: strings,
      icons: {}
    };

    if (!parts.stage || !parts.options || !parts.optionTemplate || !parts.cardTemplate) return;

    /* Option icons are the theme's own SVGs, rendered once by Liquid so the
       finder never ships a second icon set. */
    var iconSource = root.querySelector('[data-finder-icons]');
    if (iconSource) {
      Array.prototype.forEach.call(iconSource.content.querySelectorAll('[data-icon]'), function (node) {
        parts.icons[node.getAttribute('data-icon')] = node.innerHTML;
      });
    }

    var state = { answers: {}, step: 0, curated: false, currentId: null };

    function reset() {
      state.answers = {};
      state.step = 0;
      state.curated = false;
      finderRenderQuestion(root, parts, state);
    }

    parts.options.addEventListener('click', function (event) {
      var option = event.target.closest('[data-finder-option]');
      if (!option) return;

      var value = option.getAttribute('data-value');
      state.answers[state.currentId] = value;

      Array.prototype.forEach.call(parts.options.children, function (child) {
        var selected = child === option;
        child.classList.toggle('is-selected', selected);
        child.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      /* Answering a question can change what comes after it, so anything
         further down the old path is dropped. */
      var flow = finderFlow(state);
      Object.keys(state.answers).forEach(function (id) {
        if (flow.ids.indexOf(id) === -1) delete state.answers[id];
      });

      parts.next.disabled = false;
    });

    parts.next.addEventListener('click', function () {
      if (!state.answers[state.currentId]) return;

      var flow = finderFlow(state);
      var position = flow.ids.indexOf(state.currentId);
      var isLast = position === flow.ids.length - 1;

      if (isLast) {
        finderBuildEdit(root, parts, state);
        return;
      }

      state.step = position + 1;
      finderRenderQuestion(root, parts, state);
    });

    parts.back.addEventListener('click', function () {
      if (state.step === 0) return;
      state.step -= 1;
      finderRenderQuestion(root, parts, state);
    });

    if (parts.restart) {
      parts.restart.addEventListener('click', function () {
        parts.edit.hidden = true;
        reset();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    finderRenderQuestion(root, parts, state);
  }

  document.querySelectorAll('[data-beauty-finder]').forEach(function (root) {
    finderSetup(root);
  });
})();
